import * as React from "react";
import { Avatar, AvatarFallback } from "../atoms/avatar.js";
import { Card as V2Card, CardContent as V2CardContent } from "../atoms/card.js";
import { Typography } from "../atoms/typography.js";
import { cx } from "../internal/cx.js";
import { InfoFieldRow } from "../molecules/info-field-row.js";
import type { DetailsGridRow } from "./details-grid.js";

/**
 * PersonCard — an always-expanded card for a person or entity in a people list:
 * a leading avatar (initials) or icon tile, a name (with optional inline status
 * badges) + subtitle, optional header actions (a menu), one or more read-only
 * detail sections, and an optional footer.
 *
 * Used on the Benefits Wallet "Your People" surface for both dependents (a
 * simple initials card) and beneficiaries (a type-icon card with an actions
 * menu and a per-plan allocation section), and on the owner-dashboard Team
 * roster (interactive cards with role/status badges and status footnotes).
 * Unlike `TeamMemberCard`, details are always visible — there is no collapse
 * chevron. The card carries the standard elevated card shadow.
 *
 * Purely presentational: values are pre-formatted by the caller; the avatar
 * icon, badges, section rows, and footer are slots so the component stays
 * domain-free. Pass `onActivate` to make the whole card a clickable button
 * (keyboard-operable, with a hover/focus frame); header actions inside it do
 * not trigger the activation.
 */

export interface PersonCardSection {
  /** Stable key. */
  id: string;
  /** Optional heading above the section (e.g. "Allocation"). Omit for the primary detail grid. */
  title?: React.ReactNode;
  /** Read-only rows. Label/value accept nodes, so a row can carry a badge. */
  rows: DetailsGridRow[];
  /** Desktop column count for this section's grid. Default 2. */
  columns?: 1 | 2;
  /**
   * Row layout. Default `"stacked"` (label above value). Use `"inline"` for a
   * single-line label-left / value-right row — e.g. a plan name + allocation
   * percentage. Inline rows right-align their value.
   */
  layout?: "stacked" | "inline";
}

export interface PersonCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Primary display name. */
  name: React.ReactNode;
  /** Supporting line under the name (relationship, entity type). */
  subtitle?: React.ReactNode;
  /**
   * Inline chips rendered after the name (role/status badges). Typically one or
   * more `<Badge size="xs">`. Wraps with the name on narrow widths.
   */
  badges?: React.ReactNode;
  /** Initials for the fallback avatar. Ignored when `avatar` is set. */
  initials?: string;
  /**
   * Custom leading visual in place of the initials avatar — e.g. an
   * entity-type icon. Rendered in a ~2.5rem tinted tile.
   */
  avatar?: React.ReactNode;
  /**
   * Menu or icon actions rendered on the right of the header. When the card is
   * interactive (`onActivate` set), clicks/keydowns inside this slot are stopped
   * from bubbling so they don't also trigger activation — callers don't need to
   * wrap actions themselves.
   */
  headerActions?: React.ReactNode;
  /** Detail sections, rendered top to bottom. */
  sections?: PersonCardSection[];
  /**
   * Free-form content below the sections — status footnotes, an inline
   * "Resend invite" link, policy copy, etc.
   */
  footer?: React.ReactNode;
  /**
   * When set, the whole card becomes a button: `role="button"`, keyboard
   * (Enter/Space) support, and a hover/focus frame. Use `activateLabel` to give
   * the accessible name. Header actions do not trigger it (see `headerActions`).
   */
  onActivate?: () => void;
  /** Accessible label for the activation button. Required when `onActivate` is set. */
  activateLabel?: string;
}

export const PersonCard = React.forwardRef<HTMLDivElement, PersonCardProps>(function PersonCard(
  {
    activateLabel,
    avatar,
    badges,
    className,
    footer,
    headerActions,
    initials,
    name,
    onActivate,
    sections = [],
    subtitle,
    ...props
  },
  ref,
) {
  const interactive = Boolean(onActivate);
  const interactiveProps = interactive
    ? {
        role: "button" as const,
        tabIndex: 0,
        "aria-label": activateLabel,
        onClick: onActivate,
        onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onActivate?.();
          }
        },
      }
    : {};

  return (
    <V2Card
      ref={ref}
      className={cx(
        "cw-v2-person-card",
        interactive && "cw-v2-person-card--interactive",
        className,
      )}
      {...interactiveProps}
      {...props}
    >
      <V2CardContent className="cw-v2-person-card__content">
        <div className="cw-v2-person-card__header">
          <div className="cw-v2-person-card__identity">
            {avatar ? (
              <span className="cw-v2-person-card__avatar" aria-hidden>
                {avatar}
              </span>
            ) : (
              <Avatar size="md" className="cw-v2-person-card__avatar-fallback">
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            )}
            <div className="cw-v2-person-card__identity-text">
              <div className="cw-v2-person-card__name-row">
                <Typography variant="heading-small" as="h3" className="cw-v2-person-card__name">
                  {name}
                </Typography>
                {badges}
              </div>
              {subtitle ? (
                <Typography variant="caption" muted>
                  {subtitle}
                </Typography>
              ) : null}
            </div>
          </div>
          {headerActions ? (
            // In interactive cards, keep action clicks/keydowns from bubbling up
            // to the card's own activation handler.
            <div
              className="cw-v2-person-card__actions"
              onClick={interactive ? (event) => event.stopPropagation() : undefined}
              onKeyDown={interactive ? (event) => event.stopPropagation() : undefined}
            >
              {headerActions}
            </div>
          ) : null}
        </div>

        {sections.map((section) => (
          <div key={section.id} className="cw-v2-person-card__section">
            {section.title ? (
              <Typography variant="caption" muted className="cw-v2-person-card__section-title">
                {section.title}
              </Typography>
            ) : null}
            <div
              className={cx(
                "cw-v2-person-card__grid",
                (section.columns === 1 || section.layout === "inline") &&
                  "cw-v2-person-card__grid--cols-1",
                section.layout === "inline" && "cw-v2-person-card__grid--inline",
              )}
            >
              {section.rows.map(({ id, ...row }) =>
                section.layout === "inline" ? (
                  <InfoFieldRow key={id} layout="inline" align="end" size="sm" {...row} />
                ) : (
                  <InfoFieldRow key={id} layout="stacked" size="sm" {...row} />
                ),
              )}
            </div>
          </div>
        ))}

        {footer ? <div className="cw-v2-person-card__footer">{footer}</div> : null}
      </V2CardContent>
    </V2Card>
  );
});
PersonCard.displayName = "PersonCard";
